import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Printer, ShieldCheck, Lock, Globe, Mail } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCompanyConfig } from "../../../hooks/useCompanyConfig";


/* ====== Theme helpers (ProductDetail ile aynı yaklaşım) ====== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#ffffff").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

type NavItem = { id: string; label: string };

const GizlilikPolitikasi: React.FC = () => {
  const { theme } = useTheme();
  const company = useCompanyConfig();

  const accent = theme?.accent ?? "#2563eb";
  const textPrimary = theme?.textPrimary ?? "#0f172a";
  const textSecondary = theme?.textSecondary ?? "#475569";

  const pageBg = theme?.backgroundColor || "#ffffff";
  const bgRgb = hexToRgb(pageBg);
  const isDarkBg = luminance(bgRgb) < 0.5;

  const cardBg = isDarkBg ? "rgba(17,24,39,0.92)" : "#ffffff";
  const borderCol = isDarkBg ? "rgba(255,255,255,.14)" : "rgba(15,23,42,.12)";
  const softShadow = isDarkBg
    ? "0 10px 26px rgba(0,0,0,.35)"
    : "0 10px 26px rgba(2,6,23,.08)";

  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    borderColor: borderCol,
    borderWidth: 1,
    boxShadow: softShadow,
  };
  const subtleBorder: React.CSSProperties = { borderColor: borderCol };

  const lastUpdate = "23 ŞUBAT 2026";

  const nav: NavItem[] = useMemo(
    () => [
      { id: "g1", label: "1. Veri Toplama" },
      { id: "g2", label: "2. İşleme Amaçları" },
      { id: "g3", label: "3. Aktarım" },
      { id: "g4", label: "4. Saklama Süresi" },
      { id: "g5", label: "5. Veri Güvenliği" },
      { id: "g6", label: "6. Rıza / İleti İzni" },
      { id: "g7", label: "7. Haklar (KVKK 11)" },
      { id: "g8", label: "8. Başvuru Yöntemi" },
      { id: "g9", label: "9. Değişiklikler" },
      { id: "g10", label: "10. İletişim" },
    ],
    []
  );

  const onPrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div style={{ backgroundColor: pageBg }}>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { padding: 0 !important; }
          .print-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
          a { text-decoration: none !important; color: inherit !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10 print-page">
        {/* Top bar */}
        <div className="no-print mb-4 md:mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
              style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
            >
              <ArrowLeft className="w-4 h-4" />
              Anasayfa
            </Link>

            <div className="hidden md:flex items-center gap-2 text-sm" style={{ color: textSecondary }}>
              <span className="opacity-70">/</span>
              <span>Gizlilik Politikası</span>
            </div>
          </div>

          <button
            type="button"
            onClick={onPrint}
            className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
            style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
          >
            <Printer className="w-4 h-4" />
            Yazdır
          </button>
        </div>

        {/* Header */}
        <div className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div
                className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
                style={{
                  ...subtleBorder,
                  color: textSecondary,
                  backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
                }}
              >
                <FileText className="w-4 h-4" />
                KVKK / Gizlilik
              </div>

              <h1 className="mt-3 text-2xl md:text-3xl font-extrabold leading-tight" style={{ color: textPrimary }}>
                Gizlilik Politikası
              </h1>

              <p className="mt-2 text-sm leading-6" style={{ color: textSecondary }}>
                <b style={{ color: textPrimary }}>Son Güncelleme:</b> {lastUpdate}
              </p>

              <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
                {company.companyName} (&quot;{company.companyName}&quot;, &quot;Şirket&quot;, &quot;Biz&quot; veya &quot;Bize&quot; olarak
                anılacaktır) olarak, 6698 sayılı Kişisel Verilerin Korunması Kanunu (&quot;KVKK&quot;) ve ilgili mevzuat
                uyarınca &quot;Veri Sorumlusu&quot; sıfatıyla, kişisel verilerinizin işlenmesine ilişkin aydınlatma
                yükümlülüğümüzü yerine getirmek ve gizliliğinize saygı göstermek amacıyla bu Gizlilik Politikası&apos;nı
                (&quot;Politika&quot;) hazırladık.
              </p>

              <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
                Bu Politika, <b style={{ color: textPrimary }}>{company.website}</b> (&quot;Site&quot;) adresindeki
                internet sitemizi ziyaret ettiğinizde ve/veya Site üzerinden sunduğumuz ürün satışı, teknik destek,
                danışmanlık ve bilgi paylaşımı hizmetlerini (&quot;Hizmetler&quot;) kullandığınızda, kişisel
                verilerinizin hangi amaçlarla, nasıl toplandığını, işlendiğini, saklandığını, paylaşıldığını ve
                korunduğunu açıklamaktadır.
              </p>
            </div>

            <div
              className="hidden md:flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-2 border"
              style={{
                ...subtleBorder,
                color: textSecondary,
                backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
              }}
            >
              <ShieldCheck className="w-4 h-4" style={{ color: textPrimary }} />
              Veri Sorumlusu Aydınlatması
            </div>
          </div>

          <div className="mt-5 border-t" style={subtleBorder} />

          {/* Quick nav */}
          <div className="no-print mt-4 flex flex-wrap gap-2 text-xs">
            {nav.map((x) => (
              <a
                key={x.id}
                href={`#${x.id}`}
                className="rounded-full border px-3 py-1.5 hover:opacity-90 transition"
                style={{
                  borderColor: borderCol,
                  color: textSecondary,
                  backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
                }}
              >
                {x.label}
              </a>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-6 space-y-6">
          {/* 1 */}
          <section id="g1" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                1. Toplanan Kişisel Veriler ve Toplama Yöntemleri
              </h2>
            </div>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Kişisel verileriniz, aşağıdaki yöntemlerle, otomatik veya otomatik olmayan yollarla toplanabilir:
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  Doğrudan Sizden
                </div>
                <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
                  Site üzerindeki iletişim, sipariş, teklif talep, üyelik, e-bülten kayıt formları, telefon, e-posta veya
                  sosyal medya yoluyla doğrudan iletişim kurduğunuzda; adınız, soyadınız, e-posta adresiniz, telefon
                  numaranız, firma bilgileriniz, fatura/adres bilgileriniz, ödeme bilgileriniz (kredi kartı bilgileri
                  güvenli ödeme aracısı üzerinde işlenir), teknik destek talepleriniz ve tercihleriniz.
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  Otomatik Yollarla (Teknolojik Araçlar)
                </div>
                <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
                  Site&apos;yi ziyaretiniz sırasında, tarayıcınız ve etkileşiminiz hakkında belirli veriler otomatik
                  olarak toplanır. Bu veriler; IP adresiniz, tarayıcı türünüz, işletim sisteminiz, Site&apos;ye
                  giriş-çıkış saatleriniz, sayfa görüntüleme süreleriniz, tıklama davranışlarınız ve çerezler (cookies)
                  aracılığıyla toplanan verileri içerir. Çerez politikamız için lütfen &quot;Çerez Politikası&quot;
                  sayfamızı ziyaret ediniz.
                </div>
              </div>
            </div>
          </section>

          {/* 2 */}
          <section id="g2" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              2. Kişisel Verilerin İşlenme Amaçları
            </h2>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Topladığımız kişisel verileriniz, aşağıdaki amaçlarla işlenmektedir:
            </p>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>Siparişlerinizin işleme alınması, teslimatı ve faturalandırılması.</li>
              <li>Size teknik destek, danışmanlık ve müşteri hizmetleri sunulması.</li>
              <li>Hesabınızın oluşturulması ve yönetilmesi.</li>
              <li>
                Ürün/hizmetlerimizle ilgili sizi bilgilendirmek için ticari elektronik iletiler (e-bülten, kampanya
                duyuruları) gönderilmesi (açık rızanız şartıyla).
              </li>
              <li>Site&apos;nin işlevselliğinin ve kullanıcı deneyiminin geliştirilmesi, trafik analizlerinin yapılması.</li>
              <li>Yasal yükümlülüklerimizin (vergi, ticaret hukuku vb.) yerine getirilmesi.</li>
              <li>Potansiyel hileli faaliyetlerin önlenmesi ve güvenliğin sağlanması.</li>
            </ul>
          </section>

          {/* 3 */}
          <section id="g3" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                3. İşlenen Kişisel Verilerin Aktarılması
              </h2>
            </div>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Kişisel verileriniz, yukarıda belirtilen amaçları gerçekleştirebilmek için, yasal zorunluluklar çerçevesinde
              veya hizmet aldığımız iş ortaklarımızla sınırlı olmak kaydıyla, aşağıdaki yerli ve yabancı taraflara
              aktarılabilir:
            </p>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>
                <b style={{ color: textPrimary }}>Ödeme Hizmet Sağlayıcıları:</b> (Örn: Paytr, Havale) - Sipariş
                ödemelerinizin güvenli bir şekilde tahsil edilmesi amacıyla.
              </li>
              <li>
                <b style={{ color: textPrimary }}>Kargo ve Lojistik Firmaları:</b> (Örn: Aras kargo, Dhl ve Yurtiçi
                Kargo) - Siparişlerinizin size teslim edilebilmesi amacıyla.
              </li>
              <li>
                <b style={{ color: textPrimary }}>E-Posta Pazarlama ve CRM Hizmet Sağlayıcıları:</b> (Örn: Mailchimp,
                Sendinblue) - Ticari iletilerin gönderilmesi amacıyla (açık rızanız şartıyla).
              </li>
              <li>
                <b style={{ color: textPrimary }}>Web Analiz ve Performans Sağlayıcıları:</b> (Örn: Google Analytics) -
                Site performansının ölçülmesi amacıyla.
              </li>
              <li>
                <b style={{ color: textPrimary }}>Muhasebe ve Mali Müşavirler:</b> Yasal mali yükümlülüklerin yerine
                getirilmesi amacıyla.
              </li>
              <li>
                <b style={{ color: textPrimary }}>Resmi Makamlar:</b> Yasa ve düzenlemelerle yetkili kılınan kamu kurum
                ve kuruluşlarına, ancak yasal bir talebin varlığı halinde.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section id="g4" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              4. Kişisel Verilerin Saklanma Süresi
            </h2>
            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Kişisel verileriniz, işlendikleri amaç için gerekli olan ve mevzuatta öngörülen süreler boyunca saklanır.
              Bu süre, ilgili amaç geçerliliğini yitirdiğinde veya yasal saklama süreleri (örneğin Türk Ticaret Kanunu ve
              Vergi Usul Kanunu uyarınca 5 veya 10 yıl) dolduğunda sona erer. Süre sonunda verileriniz silinir, yok edilir
              veya anonim hale getirilir.
            </p>
          </section>

          {/* 5 */}
          <section id="g5" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              5. Veri Güvenliği
            </h2>
            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              {company.companyName} olarak, kişisel verilerinizi yetkisiz erişim, kayıp, ifşa, değişiklik veya imha risklerine karşı
              korumak için uygun teknik ve idari güvenlik önlemlerini (güvenlik duvarları, SSL sertifikası, sınırlı erişim
              protokolleri, düzenli güvenlik denetimleri vb.) almaktayız.
            </p>
          </section>

          {/* 6 */}
          <section id="g6" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              6. Aydınlatma ve Rıza (Ticari Elektronik İleti İzni)
            </h2>
            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Size pazarlama ve bilgilendirme amaçlı ticari elektronik ileti gönderebilmemiz için açık rızanız gereklidir.
              Bu rızayı, e-bülten kayıt formumuzdaki ilgili kutuyu (checkbox) işaretleyerek verirsiniz. Her ileti içinde,
              ileti alımınızdan ücretsiz ve kolayca çıkabilmeniz için bir &quot;abonelikten çık&quot; (unsubscribe)
              bağlantısı bulunur. SMS için &quot;DUR&quot; yanıtını gönderebilirsiniz.
            </p>
          </section>

          {/* 7 */}
          <section id="g7" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              7. Veri Sahibi Olarak Haklarınız (KVKK Md. 11)
            </h2>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              KVKK&apos;nın 11. maddesi uyarınca, kişisel verilerinizle ilgili olarak aşağıdaki haklara sahipsiniz:
            </p>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme,</li>
              <li>İşlenmişse buna ilişkin bilgi talep etme,</li>
              <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,</li>
              <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme,</li>
              <li>Eksik veya yanlış işlenmiş olması hâlinde düzeltilmesini isteme,</li>
              <li>KVKK’nın 7. maddesinde öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme,</li>
              <li>Düzeltme, silme veya yok etme işlemlerinin, verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme,</li>
              <li>
                İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir
                sonucun ortaya çıkmasına itiraz etme,
              </li>
              <li>Kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız hâlinde zararın giderilmesini talep etme.</li>
            </ul>
          </section>

          {/* 8 */}
          <section id="g8" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              8. Haklarınızı Kullanma Yöntemi
            </h2>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Yukarıda belirtilen haklarınızı kullanmak için, kimliğinizi teyit edici belgelerinizle birlikte, KVKK
              Başvuru Formu&apos;muzu doldurarak aşağıdaki yollardan biriyle bize ulaştırabilirsiniz. Talebiniz, niteliğine
              göre en geç otuz (30) gün içinde ücretsiz olarak sonuçlandırılacaktır. Ancak, işlemin ayrıca bir maliyet
              gerektirmesi halinde, Kişisel Verileri Koruma Kurumu tarafından belirlenen tarife üzerinden ücret talep
              edilebilecektir.
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  Yazılı Başvuru
                </div>
                <div className="mt-1 font-extrabold" style={{ color: textPrimary }}>
                  {company.addressLine}
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  E-Posta
                </div>
                <div className="mt-1 font-extrabold" style={{ color: textPrimary }}>
                  {company.mailDisplay}
                </div>
              </div>
            </div>
          </section>

          {/* 9 */}
          <section id="g9" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              9. Değişiklikler
            </h2>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Bu Gizlilik Politikası&apos;nı, yasal düzenlemelerdeki değişiklikler veya iş süreçlerimizdeki güncellemeler
              nedeniyle değiştirme hakkımız saklıdır. Güncellenmiş Politika, Site&apos;mizde yayınlandığı tarihte
              yürürlüğe girer. Önemli değişiklikler için sizi e-posta yoluyla bilgilendirebiliriz.
            </p>
          </section>

          {/* 10 */}
          <section id="g10" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                10. İletişim
              </h2>
            </div>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Kişisel verilerinizin işlenmesi ve haklarınızla ilgili her türlü sorunuz için aşağıdaki iletişim
              bilgilerimizden bize ulaşabilirsiniz:
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  {company.companyName}
                </div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  <div>
                    <b style={{ color: textPrimary }}>Adres:</b> {company.addressLine}
                  </div>
                  <div>
                    <b style={{ color: textPrimary }}>Telefon:</b> {company.phoneDisplay}
                  </div>
                  <div>
                    <b style={{ color: textPrimary }}>E-posta:</b> {company.mailDisplay}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  Diğer
                </div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  <div>
                    <b style={{ color: textPrimary }}>KEP:</b>{" "}
                  </div>
                  <div>
                    <b style={{ color: textPrimary }}>Web:</b> {company.website}
                  </div>
                </div>
              </div>
            </div>

            <div className="no-print mt-5 flex flex-wrap gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold"
                style={{ borderColor: borderCol, color: textPrimary, backgroundColor: cardBg }}
              >
                <ArrowLeft className="w-4 h-4" />
                Siteye dön
              </Link>
            </div>
          </section>

          {/* Accent footer strip */}
          <div
            className="h-1.5 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${String(accent)}, rgba(249,115,22,0.9))`,
              opacity: isDarkBg ? 0.9 : 1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default GizlilikPolitikasi;